import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import BlogForm from '../../components/BlogForm';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [blog, setBlog] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        if (id) {
          const response = await axios.get(`/api/v2/blog/${id}`);
          setBlog(response.data);
        }
        setInitialLoading(false);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Failed to load blog post');
        setInitialLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError('');
    
    try {
      // Create FormData object for multipart/form-data
      const form = new FormData();
      form.append('title', formData.title);
      form.append('content', formData.content);
      form.append('summary', formData.summary);
      form.append('category', formData.category);
      form.append('readTime', formData.readTime);
      form.append('status', formData.status);
      
      if (formData.image instanceof File) {
        form.append('image', formData.image);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      let response;
      if (id) {
        response = await axios.put(`/api/v2/blog/${id}`, form, config);
      } else {
        response = await axios.post('/api/v2/blog', form, config);
      }

      console.log('Blog saved successfully:', response.data);
      setSuccessMessage('Blog post saved successfully!');
      setTimeout(() => {
        setSuccessMessage('');
        navigate('/admin/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error saving blog:', error.response?.data || error);
      setError(error.response?.data?.message || 'Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ bgcolor: '#1a237e', mb: 3 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {id ? 'Edit Blog Post' : 'New Blog Post'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <BlogForm
            initialData={blog}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default BlogEditor;
